import React from 'react';

import { CustomFieldTypes } from 'common/customPostFields/Constants';
import translateString from 'common/i18n/translateString';
import Flex from 'common/ui/Flex';
import Field from 'common/uiV3/Field';
import { Select } from 'common/uiV3/Select';

import type { Board } from 'common/api/endpoints/boards';
import type { BoardField } from 'common/api/resources/postFields';
import type { FormErrors, PostForm } from 'common/post/CreatePostFormV2/utils';
import type { Option } from 'common/ui/common/select/SelectCommon';
import type { SelectBaseOption } from 'common/uiV3/Select/common';

type Props = {
  board: Board;
  onFormChange: (edits: Partial<PostForm>) => void;
  form: PostForm;
  errors: FormErrors;
};

const UncategorizedCategory: SelectBaseOption = {
  label: 'Uncategorized',
  value: '',
};

const toOption = (value?: string | string[] | null): Option | undefined => {
  if (!value || typeof value !== 'string') {
    return undefined;
  }

  return {
    label: value,
    value,
  };
};

const toOptions = (values?: string | string[] | null): Array<SelectBaseOption> => {
  if (!Array.isArray(values)) {
    return [];
  }

  return values.map((value) => ({
    label: value,
    value,
  }));
};

const PostFieldDropdowns = ({ board, onFormChange, form, errors }: Props) => {
  const boardFieldsWithOptions = board.boardFields
    .filter((boardField) => CustomFieldTypes[boardField.type]?.hasOptions)
    .sort((a, b) => a.order - b.order);

  return (
    <div className="@container">
      <Flex className="grid grid-cols-1 gap-2.5 @md:grid-cols-2 @3xl:grid-cols-3">
        <PostFieldCategory board={board} onFormChange={onFormChange} form={form} />
        {boardFieldsWithOptions.map((boardField) =>
          boardField.type === CustomFieldTypes.dropdown.name ? (
            <PostFieldDropdown
              key={boardField._id}
              boardField={boardField}
              onFormChange={onFormChange}
              form={form}
              errors={errors}
            />
          ) : (
            <PostFieldMultiSelect
              key={boardField._id}
              boardField={boardField}
              onFormChange={onFormChange}
              form={form}
              errors={errors}
            />
          ),
        )}
      </Flex>
    </div>
  );
};

const PostFieldCategory = ({
  board,
  onFormChange,
  form,
}: {
  board: Board;
  onFormChange: (edits: Partial<PostForm>) => void;
  form: PostForm;
}) => {
  if (board.categories.length === 0) {
    return null;
  }

  const categoryOptions = board.categories
    .map(
      (category): SelectBaseOption => ({
        label: `${category.parentID ? '\u2800' : ''}${translateString(category, 'name')}`,
        value: category._id,
        searchLabel: category.name,
      }),
    )
    .concat(UncategorizedCategory);

  const selectedCategory = categoryOptions.find((option) => option.value === form.categoryID);

  return (
    <Field className="flex-1">
      <Field.Label>Category</Field.Label>
      <Field.Content>
        <Select
          onValueChange={(value) => onFormChange({ categoryID: value })}
          placeholder="Select a category"
          value={selectedCategory?.value}
          options={categoryOptions}
          fullWidth
        />
      </Field.Content>
    </Field>
  );
};

const PostFieldMultiSelect = ({
  boardField,
  onFormChange,
  form,
  errors,
}: {
  boardField: BoardField;
  onFormChange: (edits: Partial<PostForm>) => void;
  form: PostForm;
  errors: FormErrors;
}) => {
  const error = errors.customFieldValuesMap?.[boardField.customPostFieldID];

  return (
    <Field key={boardField._id} className="flex-1">
      <Field.Label required={boardField.required}>{boardField.label}</Field.Label>
      <Field.Content>
        <Select
          onValueChange={(values) =>
            onFormChange({
              customFieldValuesMap: {
                ...form.customFieldValuesMap,
                [boardField.customPostFieldID]: values.length ? values : null,
              },
            })
          }
          options={toOptions(boardField.options)}
          placeholder={boardField.placeholder}
          aria-invalid={!!error}
          aria-required={boardField.required}
          fullWidth
          isMultiSelect
          hideSelection
          maxVisible={2}
          allowClear={!boardField.required}
          value={toOptions(form.customFieldValuesMap[boardField.customPostFieldID]).map(
            (option) => option.value,
          )}
        />
        <Field.Error errors={error ? [{ message: error }] : undefined} />
      </Field.Content>
    </Field>
  );
};

const PostFieldDropdown = ({
  boardField,
  onFormChange,
  form,
  errors,
}: {
  boardField: BoardField;
  onFormChange: (edits: Partial<PostForm>) => void;
  form: PostForm;
  errors: FormErrors;
}) => {
  if (!boardField.options) {
    return null;
  }

  const error = errors.customFieldValuesMap?.[boardField.customPostFieldID];

  return (
    <Field key={boardField._id} className="w-full lg:flex-1">
      <Field.Label required={boardField.required}>{boardField.label}</Field.Label>
      <Field.Content>
        <Select
          onValueChange={(value) =>
            onFormChange({
              customFieldValuesMap: {
                ...form.customFieldValuesMap,
                [boardField.customPostFieldID]: value || null,
              },
            })
          }
          fullWidth
          options={toOptions(boardField.options)}
          placeholder={boardField.placeholder}
          aria-invalid={!!error}
          aria-required={boardField.required}
          allowClear={!boardField.required}
          value={toOption(form.customFieldValuesMap[boardField.customPostFieldID])?.value}
        />
        <Field.Error errors={error ? [{ message: error }] : undefined} />
      </Field.Content>
    </Field>
  );
};

export default PostFieldDropdowns;
